import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PregnancyHealth.css';
import { useProfile } from '../../../context/ProfileContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartPulse,
  faStopwatch,
  faLightbulb,
  faGauge,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faXmark,
  faSpinner,
  faCalendarCheck,
  faArrowRight,
  faBaby,
  faPlay,
  faStop,
  faHandPointer,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { Container } from 'react-bootstrap';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import ProfileStrip from '../../../components/ProfileStrip/ProfileStrip';
import womensHealthService, {
  PregnancyProfile,
  PregnancyCheckup,
  KickCounter,
} from '../../../services/womensHealthService';

type HealthTab = 'checkups' | 'kick-counter' | 'symptom-tips' | 'vitals-info';

interface ToastState {
  message: string;
  type: 'success' | 'error';
}

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                          */
/* ------------------------------------------------------------------ */

const PregnancyHealth: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  const [pregnancy, setPregnancy] = useState<PregnancyProfile | null>(null);
  const [checkups, setCheckups] = useState<PregnancyCheckup[]>([]);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [checkupChoices, setCheckupChoices] = useState<any>(null);
  const [kickCounters, setKickCounters] = useState<KickCounter[]>([]);
  const [activeKick, setActiveKick] = useState<KickCounter | null>(null);
  const [loading, setLoading] = useState(true);
  // When arriving via the "Appointments & Reminders" card, focus on checkups only
  const appointmentsFocus = searchParams.get('focus') === 'appointments';
  const [activeTab, setActiveTab] = useState<HealthTab>(
    appointmentsFocus ? 'checkups' : ((searchParams.get('tab') as HealthTab) || 'checkups')
  );
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clientStartRef = useRef<number | null>(null);

  // Checkup form
  const [showCheckupForm, setShowCheckupForm] = useState(false);
  const [checkupForm, setCheckupForm] = useState({
    checkup_type: '',
    date: '',
    doctor_name: '',
    hospital_name: '',
    notes: '',
    next_appointment: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Checkup edit
  const [editingCheckupId, setEditingCheckupId] = useState<number | null>(null);
  const [editCheckupForm, setEditCheckupForm] = useState({
    checkup_type: '',
    date: '',
    doctor_name: '',
    hospital_name: '',
    notes: '',
    next_appointment: '',
  });

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const preg = await womensHealthService.getActivePregnancy();
      setPregnancy(preg);

      if (preg) {
        const [checks, upcoming, cChoices, kicks, latestKick] = await Promise.all([
          womensHealthService.getPregnancyCheckups(preg.id),
          womensHealthService.getUpcomingCheckups(preg.id),
          womensHealthService.getPregnancyCheckupChoices(),
          womensHealthService.getKickCounters(preg.id),
          womensHealthService.getKickCounterLatest(preg.id),
        ]);
        setCheckups(Array.isArray(checks) ? checks : []);
        setUpcomingCount(Array.isArray(upcoming) ? upcoming.length : 0);
        setCheckupChoices(cChoices);
        setKickCounters(Array.isArray(kicks) ? kicks : []);

        if (latestKick && !latestKick.end_time) {
          const startedAt = new Date(latestKick.start_time).getTime();
          const ageMs = Date.now() - startedAt;
          if (ageMs > 2 * 60 * 60 * 1000) {
            try {
              await womensHealthService.endKickSession(latestKick.id);
              const refreshed = await womensHealthService.getKickCounters(preg.id);
              setKickCounters(Array.isArray(refreshed) ? refreshed : []);
            } catch {
              // Non-fatal: continue with no active session.
            }
            setActiveKick(null);
          } else {
            setActiveKick(latestKick);
          }
        } else {
          setActiveKick(null);
        }
      }
    } catch {
      // Silently handle - pregnancy might not exist
    } finally {
      setLoading(false);
    }
  }, [activeProfile]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Elapsed timer for active kick counter
  useEffect(() => {
    if (activeKick && !activeKick.end_time) {
      const startTime = clientStartRef.current ?? new Date(activeKick.start_time).getTime();
      const updateElapsed = () => {
        // Clamp to 0 to guard against small server/client clock skew so a
        // freshly started session never displays as already elapsed.
        setElapsedTime(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));
      };
      updateElapsed();
      timerRef.current = setInterval(updateElapsed, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else {
      setElapsedTime(0);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [activeKick]);

  const formatElapsed = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const formatDurationMinutes = (mins: number | null | undefined) => {
    const value = Math.max(0, Number(mins) || 0);
    const totalSeconds = Math.round(value * 60);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    if (m === 0) return `${s} sec`;
    if (s === 0) return `${m} min`;
    return `${m} min ${s} sec`;
  };

  /* --- Checkup handlers --- */
  const handleCheckupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pregnancy) return;
    setSubmitting(true);
    try {
      await womensHealthService.createPregnancyCheckup({
        pregnancy: pregnancy.id,
        ...checkupForm,
        next_appointment: checkupForm.next_appointment || null,
      });
      showToast('Checkup added successfully!', 'success');
      setShowCheckupForm(false);
      setCheckupForm({ checkup_type: '', date: '', doctor_name: '', hospital_name: '', notes: '', next_appointment: '' });
      const [checks, upcoming] = await Promise.all([
        womensHealthService.getPregnancyCheckups(pregnancy.id),
        womensHealthService.getUpcomingCheckups(pregnancy.id),
      ]);
      setCheckups(Array.isArray(checks) ? checks : []);
      setUpcomingCount(Array.isArray(upcoming) ? upcoming.length : 0);
    } catch {
      showToast('Failed to add checkup.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckupDelete = (id: number) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Checkup',
      message: 'Are you sure you want to delete this checkup?',
      onConfirm: async () => {
        try {
          await womensHealthService.deletePregnancyCheckup(id);
          showToast('Checkup deleted.', 'success');
          if (pregnancy) {
            const [checks, upcoming] = await Promise.all([
              womensHealthService.getPregnancyCheckups(pregnancy.id),
              womensHealthService.getUpcomingCheckups(pregnancy.id),
            ]);
            setCheckups(Array.isArray(checks) ? checks : []);
            setUpcomingCount(Array.isArray(upcoming) ? upcoming.length : 0);
          }
        } catch {
          showToast('Failed to delete checkup.', 'error');
        }
      },
    });
  };

  const handleCheckupEditStart = (c: PregnancyCheckup) => {
    setEditingCheckupId(c.id);
    setEditCheckupForm({
      checkup_type: c.checkup_type,
      date: c.date,
      doctor_name: c.doctor_name,
      hospital_name: c.hospital_name,
      notes: c.notes,
      next_appointment: c.next_appointment || '',
    });
  };

  const handleCheckupEditSave = async (id: number) => {
    try {
      await womensHealthService.updatePregnancyCheckup(id, {
        ...editCheckupForm,
        next_appointment: editCheckupForm.next_appointment || null,
      });
      showToast('Checkup updated.', 'success');
      setEditingCheckupId(null);
      if (pregnancy) {
        const checks = await womensHealthService.getPregnancyCheckups(pregnancy.id);
        setCheckups(Array.isArray(checks) ? checks : []);
      }
    } catch {
      showToast('Failed to update checkup.', 'error');
    }
  };

  /* --- Kick counter handlers --- */
  const handleStartKick = async () => {
    if (!pregnancy) return;
    try {
      const clickedAt = Date.now();
      const kick = await womensHealthService.createKickCounter({
        pregnancy: pregnancy.id,
        start_time: new Date(clickedAt).toISOString(),
      });
      clientStartRef.current = clickedAt;
      setElapsedTime(0);
      setActiveKick(kick);
      showToast('Kick counting session started!', 'success');
    } catch {
      showToast('Failed to start session.', 'error');
    }
  };

  const handleRecordKick = async () => {
    if (!activeKick) return;
    try {
      const updated = await womensHealthService.incrementKick(activeKick.id);
      setActiveKick(updated);
    } catch {
      showToast('Failed to record kick.', 'error');
    }
  };

  const handleEndSession = async () => {
    if (!activeKick || !pregnancy) return;
    try {
      await womensHealthService.endKickSession(activeKick.id);
      clientStartRef.current = null;
      setActiveKick(null);
      showToast('Session ended.', 'success');
      const kicks = await womensHealthService.getKickCounters(pregnancy.id);
      setKickCounters(Array.isArray(kicks) ? kicks : []);
    } catch {
      showToast('Failed to end session.', 'error');
    }
  };

  const handleDeleteKick = (id: number) => {
    if (!pregnancy) return;
    const pregnancyId = pregnancy.id;
    setConfirmDialog({
      open: true,
      title: 'Delete Session',
      message: 'Are you sure you want to delete this kick session?',
      onConfirm: async () => {
        try {
          await womensHealthService.deleteKickCounter(id);
          showToast('Session deleted.', 'success');
          const kicks = await womensHealthService.getKickCounters(pregnancyId);
          setKickCounters(Array.isArray(kicks) ? kicks : []);
        } catch {
          showToast('Failed to delete session.', 'error');
        }
      },
    });
  };

  const allTabs = [
    { id: 'checkups' as HealthTab, label: 'Checkups', icon: faCalendarCheck },
    { id: 'kick-counter' as HealthTab, label: 'Kick Counter', icon: faStopwatch },
    { id: 'symptom-tips' as HealthTab, label: 'Symptom Tips', icon: faLightbulb },
    { id: 'vitals-info' as HealthTab, label: 'Vitals Info', icon: faGauge },
  ];
  // In appointments-focus mode, only the Checkups (appointments) tab is relevant
  const tabs = appointmentsFocus ? allTabs.filter((tab) => tab.id === 'checkups') : allTabs;

  /* ---- Loading state ---- */
  if (loading) {
    return (
      <div className="wh-ph-page">
        <section className="wh-ph-banner">
          <Container>
            <BackButton />
            <div className="wh-ph-banner-content">
              <div className="wh-ph-banner-icon"><FontAwesomeIcon icon={faHeartPulse} /></div>
              <div>
                <h1 className="wh-ph-banner-title">{t('womensHealth:pregnancyHealth.title')}</h1>
                <p className="wh-ph-banner-subtitle">Monitor your health throughout your pregnancy</p>
              </div>
            </div>
          </Container>
        </section>
        <Container>
          <div className="wh-ph-loading">
            <FontAwesomeIcon icon={faSpinner} spin className="wh-ph-loading-icon" />
            <p>Loading health data...</p>
          </div>
        </Container>
      </div>
    );
  }

  /* ---- No active pregnancy ---- */
  if (!pregnancy) {
    return (
      <div className="wh-ph-page">
        <section className="wh-ph-banner">
          <Container>
            <BackButton />
            <div className="wh-ph-banner-content">
              <div className="wh-ph-banner-icon"><FontAwesomeIcon icon={faHeartPulse} /></div>
              <div>
                <h1 className="wh-ph-banner-title">{t('womensHealth:pregnancyHealth.title')}</h1>
                <p className="wh-ph-banner-subtitle">Monitor your health throughout your pregnancy</p>
              </div>
            </div>
          </Container>
        </section>
        <Container>
          <div className="wh-ph-empty-state">
            <div className="wh-ph-empty-icon"><FontAwesomeIcon icon={faBaby} /></div>
            <h3>No Active Pregnancy Found</h3>
            <p>Start your pregnancy profile first to access health monitoring tools.</p>
            <a href="/womens-health/pregnancy" className="wh-ph-link-btn">
              <FontAwesomeIcon icon={faArrowRight} /> Go to Pregnancy Dashboard
            </a>
          </div>
        </Container>
      </div>
    );
  }

  /* ---- Checkups Tab ---- */
  const renderCheckups = () => (
    <div className="wh-ph-checkups">
      {upcomingCount > 0 && (
        <div className="wh-ph-upcoming-badge">
          <FontAwesomeIcon icon={faCalendarCheck} />
          <span>You have <strong>{upcomingCount}</strong> upcoming checkup{upcomingCount > 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="wh-ph-section-header-row">
        <h3>Your Checkups</h3>
        <button className="wh-ph-add-btn" onClick={() => setShowCheckupForm(!showCheckupForm)}>
          <FontAwesomeIcon icon={showCheckupForm ? faXmark : faPlus} />
          {showCheckupForm ? 'Cancel' : 'Add Checkup'}
        </button>
      </div>

      {showCheckupForm && (
        <form className="wh-ph-form-card" onSubmit={handleCheckupSubmit}>
          <div className="wh-ph-form-row">
            <div className="wh-ph-form-group">
              <label>Checkup Type</label>
              <select
                value={checkupForm.checkup_type}
                onChange={(e) => setCheckupForm({ ...checkupForm, checkup_type: e.target.value })}
                className="wh-ph-select"
                required
              >
                <option value="">Select type</option>
                {checkupChoices?.checkup_type?.map((c: any) => (
                  <option key={c.value} value={c.value}>{c.display_name}</option>
                ))}
              </select>
            </div>
            <div className="wh-ph-form-group">
              <label>Date</label>
              <input
                type="date"
                value={checkupForm.date}
                onChange={(e) => setCheckupForm({ ...checkupForm, date: e.target.value })}
                className="wh-ph-input"
                required
              />
            </div>
          </div>
          <div className="wh-ph-form-row">
            <div className="wh-ph-form-group">
              <label>Doctor Name</label>
              <input
                type="text"
                placeholder="Dr. Smith"
                value={checkupForm.doctor_name}
                onChange={(e) => setCheckupForm({ ...checkupForm, doctor_name: e.target.value })}
                className="wh-ph-input"
              />
            </div>
            <div className="wh-ph-form-group">
              <label>Hospital Name</label>
              <input
                type="text"
                placeholder="City Hospital"
                value={checkupForm.hospital_name}
                onChange={(e) => setCheckupForm({ ...checkupForm, hospital_name: e.target.value })}
                className="wh-ph-input"
              />
            </div>
          </div>
          <div className="wh-ph-form-row">
            <div className="wh-ph-form-group">
              <label>Notes</label>
              <textarea
                rows={2}
                placeholder="Any notes about this checkup..."
                value={checkupForm.notes}
                onChange={(e) => setCheckupForm({ ...checkupForm, notes: e.target.value })}
                className="wh-ph-textarea"
              />
            </div>
            <div className="wh-ph-form-group">
              <label>Next Appointment</label>
              <input
                type="date"
                value={checkupForm.next_appointment}
                onChange={(e) => setCheckupForm({ ...checkupForm, next_appointment: e.target.value })}
                className="wh-ph-input"
              />
            </div>
          </div>
          <button type="submit" className="wh-ph-submit-btn" disabled={submitting}>
            {submitting ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faCheck} /> Save Checkup</>}
          </button>
        </form>
      )}

      {checkups.length === 0 ? (
        <div className="wh-ph-empty-state-sm">
          <p>No checkups recorded yet. Add your first checkup above.</p>
        </div>
      ) : (
        <div className="wh-ph-checkup-list">
          {checkups.map((c) => (
            <div className="wh-ph-checkup-card" key={c.id}>
              {editingCheckupId === c.id ? (
                <div className="wh-ph-checkup-edit-form">
                  <div className="wh-ph-form-row">
                    <div className="wh-ph-form-group">
                      <label>Type</label>
                      <select value={editCheckupForm.checkup_type} onChange={(e) => setEditCheckupForm({ ...editCheckupForm, checkup_type: e.target.value })} className="wh-ph-select">
                        {checkupChoices?.checkup_type?.map((ch: any) => (
                          <option key={ch.value} value={ch.value}>{ch.display_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="wh-ph-form-group">
                      <label>Date</label>
                      <input type="date" value={editCheckupForm.date} onChange={(e) => setEditCheckupForm({ ...editCheckupForm, date: e.target.value })} className="wh-ph-input" />
                    </div>
                  </div>
                  <div className="wh-ph-form-row">
                    <div className="wh-ph-form-group">
                      <label>Doctor</label>
                      <input type="text" value={editCheckupForm.doctor_name} onChange={(e) => setEditCheckupForm({ ...editCheckupForm, doctor_name: e.target.value })} className="wh-ph-input" />
                    </div>
                    <div className="wh-ph-form-group">
                      <label>Hospital</label>
                      <input type="text" value={editCheckupForm.hospital_name} onChange={(e) => setEditCheckupForm({ ...editCheckupForm, hospital_name: e.target.value })} className="wh-ph-input" />
                    </div>
                  </div>
                  <div className="wh-ph-form-row">
                    <div className="wh-ph-form-group">
                      <label>Notes</label>
                      <textarea
                        rows={2}
                        placeholder="Any notes about this checkup..."
                        value={editCheckupForm.notes}
                        onChange={(e) => setEditCheckupForm({ ...editCheckupForm, notes: e.target.value })}
                        className="wh-ph-textarea"
                      />
                    </div>
                    <div className="wh-ph-form-group">
                      <label>Next Appointment</label>
                      <input
                        type="date"
                        value={editCheckupForm.next_appointment}
                        onChange={(e) => setEditCheckupForm({ ...editCheckupForm, next_appointment: e.target.value })}
                        className="wh-ph-input"
                      />
                    </div>
                  </div>
                  <div className="wh-ph-edit-actions">
                    <button className="wh-ph-action-btn wh-ph-save-btn" onClick={() => handleCheckupEditSave(c.id)}>
                      <FontAwesomeIcon icon={faCheck} /> Save
                    </button>
                    <button className="wh-ph-action-btn wh-ph-cancel-btn" onClick={() => setEditingCheckupId(null)}>
                      <FontAwesomeIcon icon={faXmark} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="wh-ph-checkup-top">
                    <span className="wh-ph-checkup-type-badge">{c.checkup_type_display || c.checkup_type}</span>
                    <span className="wh-ph-checkup-date">{formatDate(c.date)}</span>
                  </div>
                  <div className="wh-ph-checkup-details">
                    {c.doctor_name && <p><strong>Doctor:</strong> {c.doctor_name}</p>}
                    {c.hospital_name && <p><strong>Hospital:</strong> {c.hospital_name}</p>}
                    {c.next_appointment && <p><strong>Next Appt:</strong> {formatDate(c.next_appointment)}</p>}
                    {c.notes && <p className="wh-ph-checkup-notes">{c.notes}</p>}
                  </div>
                  <div className="wh-ph-checkup-actions">
                    <button className="wh-ph-icon-btn wh-ph-edit-icon" onClick={() => handleCheckupEditStart(c)}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button className="wh-ph-icon-btn wh-ph-delete-icon" onClick={() => handleCheckupDelete(c.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ---- Kick Counter Tab ---- */
  const renderKickCounter = () => (
    <div className="wh-ph-kick-counter">
      {activeKick && !activeKick.end_time ? (
        <div className="wh-ph-kick-active">
          <div className="wh-ph-kick-circle">
            <span className="wh-ph-kick-count">{activeKick.kick_count}</span>
            <span className="wh-ph-kick-label">kicks</span>
          </div>
          <div className="wh-ph-kick-elapsed">
            <FontAwesomeIcon icon={faClock} />
            <span>{formatElapsed(elapsedTime)}</span>
          </div>
          <div className="wh-ph-kick-buttons">
            <button className="wh-ph-kick-record-btn" onClick={handleRecordKick}>
              <FontAwesomeIcon icon={faHandPointer} />
              {t('womensHealth:pregnancy.logKick')}
            </button>
            <button className="wh-ph-kick-end-btn" onClick={handleEndSession}>
              <FontAwesomeIcon icon={faStop} />
              End Session
            </button>
          </div>
        </div>
      ) : (
        <div className="wh-ph-kick-start">
          <div className="wh-ph-kick-start-icon">
            <FontAwesomeIcon icon={faStopwatch} />
          </div>
          <h3>Ready to Count?</h3>
          <p>Track your baby's movements by starting a kick counting session. Aim for 10 kicks within 2 hours.</p>
          <button className="wh-ph-kick-start-btn" onClick={handleStartKick}>
            <FontAwesomeIcon icon={faPlay} />
            Start New Session
          </button>
        </div>
      )}

      {kickCounters.filter((k) => k.end_time).length > 0 && (
        <div className="wh-ph-kick-history">
          <h4>Session History</h4>
          <div className="wh-ph-kick-history-list">
            {kickCounters
              .filter((k) => k.end_time)
              .map((k) => (
                <div className="wh-ph-kick-history-item" key={k.id}>
                  <div className="wh-ph-kick-history-info">
                    <span className="wh-ph-kick-history-date">{formatDate(k.start_time)}</span>
                    <span className="wh-ph-kick-history-time">{formatTime(k.start_time)}</span>
                  </div>
                  <div className="wh-ph-kick-history-stats">
                    <span className="wh-ph-kick-stat"><strong>{k.kick_count}</strong> kicks</span>
                    {k.duration_minutes !== null && (
                      <span className="wh-ph-kick-stat">
                        <FontAwesomeIcon icon={faClock} /> {formatDurationMinutes(k.duration_minutes)}
                      </span>
                    )}
                  </div>
                  <button className="wh-ph-icon-btn wh-ph-delete-icon" onClick={() => handleDeleteKick(k.id)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );

  /* ---- Symptom Tips Tab ---- */
  const renderSymptomTips = () => (
    <div className="wh-ph-symptom-tips">
      <WHArticleList
        stage="pregnancy"
        contentType="symptom_tips"
        variant="card"
        icon={faLightbulb}
        accentColor="#f59e0b"
      />
    </div>
  );

  /* ---- Vitals Info Tab ---- */
  const renderVitalsInfo = () => (
    <div
      className="wh-ph-vitals-info"
      style={{ display: "flex", flexDirection: "column", gap: "16px" }}
    >
      <WHArticleList
        stage="pregnancy"
        contentType="vitals_info"
        variant="card"
        icon={faGauge}
        accentColor="#ef4444"
      />

      <div className="wh-ph-vitals-cta">
        <FontAwesomeIcon icon={faHeartPulse} className="wh-ph-cta-icon" />
        <div className="wh-ph-cta-text">
          <h4>Track Your Vitals</h4>
          <p>
            Visit the Health Records section to log your blood pressure,
            glucose, and other health metrics regularly.
          </p>
        </div>

        <a href="/health-records" className="wh-ph-link-btn">
          Health Records
        </a>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'checkups': return renderCheckups();
      case 'kick-counter': return renderKickCounter();
      case 'symptom-tips': return renderSymptomTips();
      case 'vitals-info': return renderVitalsInfo();
      default: return null;
    }
  };

  return (
    <div className="wh-ph-page">
      <section className="wh-ph-banner">
        <Container>
          <BackButton />
          <div className="wh-ph-banner-content">
            <div className="wh-ph-banner-icon"><FontAwesomeIcon icon={faHeartPulse} /></div>
            <div>
              <h1 className="wh-ph-banner-title">{t('womensHealth:pregnancyHealth.title')}</h1>
              <p className="wh-ph-banner-subtitle">Track checkups, count kicks, and manage your health</p>
            </div>
          </div>
        </Container>
      </section>

      <Container>
        <ProfileStrip
          summary={`Week ${pregnancy.current_week ?? '—'} · LMP ${pregnancy.lmp_date ? formatDate(pregnancy.lmp_date) : '—'}`}
          hubPath="/womens-health/pregnancy"
          hubLabel="Pregnancy Dashboard"
        />
        <div className="wh-ph-tabs-wrapper">
          <div className="wh-ph-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`wh-ph-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} className="wh-ph-tab-icon" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="wh-ph-content" key={activeTab}>
          {renderTabContent()}
        </div>

        {toast && (
          <div className={`wh-ph-toast wh-ph-toast-${toast.type}`}>
            <FontAwesomeIcon icon={toast.type === 'success' ? faCheck : faXmark} />
            {toast.message}
          </div>
        )}

        {/* Confirm dialog overlay */}
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={t('common:buttons.delete')}
          cancelLabel={t('common:buttons.cancel')}
          variant="danger"
          onConfirm={confirmDialog.onConfirm}
          onClose={() => setConfirmDialog((d) => ({ ...d, open: false }))}
        />
      </Container>
    </div>
  );
};

export default PregnancyHealth;
